import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'antd';
import CurrencyInput from 'react-currency-input-field';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { COMMON_CONSTANTS, useOneButtonStore } from '../../../../store/oneButtonStore';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import { useOneButtonCapability } from '../../../../hooks/useOneButtonCapability';
import type { selectedQuote } from './FinancingGridCard';
import { GenerateQuoteSectionHead } from './GenerateQuoteSectionHead';
import type { GqSectionStat } from './GenerateQuoteSectionHead.types';
import { OBDSelect } from './form-elements';
import './oneButton-css/RoofQuoteDetails.css';
import './oneButton-css/RRQuoteDetails.css';

interface SelectOption {
    label: string;
    value: string;
}

interface RRQuoteDetailsProps {
    confirmQuoteCellKey: selectedQuote | null;
    priceQuote: any;
    theme: string;
    updateLoaderCount: (loading: boolean) => void;
}

function excelOutputValues(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.OutputValues)) return res.OutputValues;
    return [];
}

function firstCellValue(res: any, cellNumber?: string): string {
    const output = excelOutputValues(res);
    const hit = cellNumber
        ? output.find((o) => String(o?.CellNumber ?? '').toUpperCase() === cellNumber.toUpperCase())
        : output[0];
    return String(hit?.CellValue ?? hit?.cellValue ?? '').trim();
}

function pipeOptions(cellValue: string): SelectOption[] {
    if (!cellValue) return [];
    return cellValue
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => ({ label: s, value: s }));
}

function formatCurrency(val: any): string {
    const num = Number(val || 0);
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number.isFinite(num) ? num : 0);
}

function partnerDisplayFromProduct(p: any): string {
    return String(
        p?.Finance_Partner_Display_Name ??
            p?.FinancePartnerDisplayName ??
            p?.Finance_Partner ??
            p?.finance_Partner ??
            '',
    ).trim();
}

function normalizePartnerKey(value: unknown): string {
    return String(value ?? '')
        .replace(/\s+/g, '')
        .trim()
        .toLowerCase();
}

function isRROpportunityProduct(p: any): boolean {
    const name = String(p?.Product_Type_Name ?? p?.product_Type_Name ?? p?.ProductType ?? '').trim();
    return /^rr$/i.test(name);
}

function rrSystemSizeFromQuote(priceQuote: any, opportunityDetails: any): string {
    const raw = priceQuote?.RR_System_Size ?? opportunityDetails?.RR_System_Size ?? '';
    if (raw == null || raw === '') return '';
    return String(raw);
}

function computeTotalSalesAmount(pricingMethod: string, price: string | number, systemSize: string | number): number {
    const priceNum = Number(price) || 0;
    if (pricingMethod === 'EPC') return priceNum;
    const sizeNum = Number(systemSize) || 0;
    return priceNum * sizeNum * 1000;
}

function allowDecimalKey(event: React.KeyboardEvent<HTMLInputElement>): boolean {
    const charCode = event.which || event.keyCode;
    if (charCode === 46 || (charCode >= 48 && charCode <= 57) || charCode < 32) return true;
    event.preventDefault();
    return false;
}

const RRQuoteDetails: React.FC<RRQuoteDetailsProps> = ({
    confirmQuoteCellKey,
    priceQuote,
    theme,
    updateLoaderCount,
}) => {
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const updateRRDetails = useOneButtonStore((s) => s.updateRRDetails);
    const { getCapabilityValue } = useOneButtonCapability();

    const pq = priceQuote || {};
    const rrBlock = pq.RR || {};
    const installationState = String(pq.InstallationState ?? opportunityDetails?.InstallationState ?? '').trim();
    const formLocked = getCapabilityValue('UI_Utility_Info_Access') === 'View';
    const systemSizeEditable = getCapabilityValue('Quote_RR_System_Size') === 'Edit';

    const financePartners = useMemo<SelectOption[]>(() => {
        const products = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
        const seen = new Set<string>();
        const options: SelectOption[] = [];
        (Array.isArray(products) ? products : []).forEach((p: any) => {
            const id = String(p?.Finance_Partner_Id ?? p?.FinancePartnerId ?? '').trim();
            if (!id || seen.has(id)) return;
            seen.add(id);
            options.push({
                value: id,
                label: partnerDisplayFromProduct(p) || id,
            });
        });
        const clicked = String(confirmQuoteCellKey?.partner ?? '').trim();
        if (clicked && !options.some((o) => normalizePartnerKey(o.label) === normalizePartnerKey(clicked))) {
            options.push({ value: clicked, label: clicked });
        }
        return options;
    }, [opportunityDetails, confirmQuoteCellKey?.partner]);

    const initialPartnerId = useMemo(() => {
        const fromQuote = String(rrBlock.FinancePartnerId ?? '').trim();
        if (fromQuote && financePartners.some((o) => o.value === fromQuote)) return fromQuote;
        const products = opportunityDetails?.OpportunityProduct ?? [];
        const rrProduct = (Array.isArray(products) ? products : []).find(
            (p: any) => isRROpportunityProduct(p) && p?.ActiveFlag !== false,
        );
        const fromOpp = String(rrProduct?.Finance_Partner_Id ?? '').trim();
        if (fromOpp && financePartners.some((o) => o.value === fromOpp)) return fromOpp;
        const clicked = String(confirmQuoteCellKey?.partner ?? '').trim();
        const byName = financePartners.find((o) => normalizePartnerKey(o.label) === normalizePartnerKey(clicked));
        return byName?.value ?? financePartners[0]?.value ?? '';
    }, [rrBlock.FinancePartnerId, financePartners, opportunityDetails, confirmQuoteCellKey?.partner]);

    const [financePartnerId, setFinancePartnerId] = useState(initialPartnerId);
    const [financeMethodId, setFinanceMethodId] = useState(String(rrBlock.FinanceMethodId ?? rrBlock.ContractType ?? confirmQuoteCellKey?.methord ?? '').trim());
    const [pricingMethod, setPricingMethod] = useState(String(rrBlock.PricingMethod ?? '').trim());
    const [price, setPrice] = useState<string>(rrBlock.PricingValue != null && rrBlock.PricingValue !== '' ? String(rrBlock.PricingValue) : '1.00');
    const [downPayment, setDownPayment] = useState<string>(rrBlock.DownPayment != null && rrBlock.DownPayment !== '' ? String(rrBlock.DownPayment) : '0.00');
    const [depositAmount, setDepositAmount] = useState<string>(
        rrBlock.Deposit_Amount != null && rrBlock.Deposit_Amount !== '' ? String(rrBlock.Deposit_Amount) : '0.00',
    );
    const [rrSystemSize, setRrSystemSize] = useState(rrSystemSizeFromQuote(pq, opportunityDetails));
    const [financeMethods, setFinanceMethods] = useState<SelectOption[]>([]);
    const [pricingMethods, setPricingMethods] = useState<SelectOption[]>([]);
    const [totalSalesAmountShow, setTotalSalesAmountShow] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const financeMethodsLoadedForRef = useRef('');
    const pricingMethodsLoadedForRef = useRef('');

    const selectedPartner = useMemo(
        () => financePartners.find((o) => o.value === String(financePartnerId)) ?? null,
        [financePartners, financePartnerId],
    );
    const financePartnerName = selectedPartner?.label || String(confirmQuoteCellKey?.partner ?? '').trim();
    const selectDropdownClassName = `obd-select-dropdown obd-roof-quote-select-dropdown obd-theme-${theme}`;

    const totalSalesAmount = useMemo(
        () => computeTotalSalesAmount(pricingMethod, price, rrSystemSize),
        [pricingMethod, price, rrSystemSize],
    );

    const priceInvalid = Number(price) < 1;
    const systemSizeInvalid = !String(rrSystemSize ?? '').trim();
    const partnerInvalid = !String(financePartnerId ?? '').trim();
    const methodInvalid = !String(financeMethodId ?? '').trim();
    const pricingMethodInvalid = !String(pricingMethod ?? '').trim();
    const formValid = !partnerInvalid && !methodInvalid && !pricingMethodInvalid && !priceInvalid && !systemSizeInvalid;

    const validationMessage = useMemo(() => {
        if (partnerInvalid) return 'Select a finance partner before generating.';
        if (methodInvalid) return 'Select a finance method before generating.';
        if (pricingMethodInvalid) return 'Select a pricing method before generating.';
        if (priceInvalid) return 'Please enter pricing value';
        if (systemSizeInvalid) return 'Please enter R&R System Size in Kilowatts';
        return '';
    }, [partnerInvalid, methodInvalid, pricingMethodInvalid, priceInvalid, systemSizeInvalid]);

    const loadFinanceMethods = useCallback(
        async (partnerName: string) => {
            if (!partnerName || !installationState) {
                setFinanceMethods([]);
                setFinanceMethodId('');
                return;
            }
            const cacheKey = `${installationState}::${partnerName}`;
            if (financeMethodsLoadedForRef.current === cacheKey) return;
            updateLoaderCount(true);
            try {
                const res = await masterServices.cascadingDropDown({
                    ExcelFileName: 'Sunnova Financial Data',
                    SheetName: 'SVA Financing Options',
                    InputValues: [
                        { Type: 'String', CellNumber: 'B4', CellValue: installationState },
                        { Type: 'String', CellNumber: 'C4', CellValue: 'R&R' },
                        { Type: 'String', CellNumber: 'D4', CellValue: partnerName },
                        { Type: 'String', CellNumber: 'C8', CellValue: '' },
                    ],
                    OutputCells: ['E4'],
                });
                const options = pipeOptions(firstCellValue(res, 'E4'));
                financeMethodsLoadedForRef.current = cacheKey;
                setFinanceMethods(options);
                setFinanceMethodId((current) => {
                    if (current && options.some((o) => o.value === current)) return current;
                    return options[0]?.value ?? '';
                });
            } catch (err) {
                console.error('Failed to load RR finance methods', err);
                setFinanceMethods([]);
            } finally {
                updateLoaderCount(false);
            }
        },
        [installationState, updateLoaderCount],
    );

    const loadPricingMethods = useCallback(
        async (partnerName: string, methodName: string) => {
            if (!partnerName || !methodName || !installationState) {
                setPricingMethods([]);
                setPricingMethod('');
                return;
            }
            const cacheKey = `${installationState}::${partnerName}::${methodName}`;
            if (pricingMethodsLoadedForRef.current === cacheKey) return;
            updateLoaderCount(true);
            try {
                const res = await masterServices.cascadingDropDown({
                    ExcelFileName: 'Sunnova Financial Data',
                    SheetName: 'SVA Financing Options',
                    InputValues: [
                        { Type: 'String', CellNumber: 'B4', CellValue: installationState },
                        { Type: 'String', CellNumber: 'C4', CellValue: 'R&R' },
                        { Type: 'String', CellNumber: 'D4', CellValue: partnerName },
                        { Type: 'String', CellNumber: 'F4', CellValue: methodName.trim() },
                    ],
                    OutputCells: ['H4'],
                });
                const options = pipeOptions(firstCellValue(res, 'H4'));
                pricingMethodsLoadedForRef.current = cacheKey;
                setPricingMethods(options);
                setPricingMethod((current) => {
                    if (current && options.some((o) => o.value === current)) return current;
                    return options[0]?.value ?? '';
                });
                setRrSystemSize((current) => current || rrSystemSizeFromQuote(pq, opportunityDetails));
                setShowValidation(true);
            } catch (err) {
                console.error('Failed to load RR pricing methods', err);
                setPricingMethods([]);
            } finally {
                updateLoaderCount(false);
            }
        },
        [installationState, opportunityDetails, pq, updateLoaderCount],
    );

    useEffect(() => {
        if (!confirmQuoteCellKey) return;
        financeMethodsLoadedForRef.current = '';
        pricingMethodsLoadedForRef.current = '';
        setFinancePartnerId(initialPartnerId);
        setPrice(rrBlock.PricingValue != null && rrBlock.PricingValue !== '' ? String(rrBlock.PricingValue) : '1.00');
        setDownPayment(rrBlock.DownPayment != null && rrBlock.DownPayment !== '' ? String(rrBlock.DownPayment) : '0.00');
        setDepositAmount(rrBlock.Deposit_Amount != null && rrBlock.Deposit_Amount !== '' ? String(rrBlock.Deposit_Amount) : '0.00');
        setRrSystemSize(rrSystemSizeFromQuote(pq, opportunityDetails));
    }, [confirmQuoteCellKey?.partner, confirmQuoteCellKey?.product, initialPartnerId]);

    useEffect(() => {
        if (!financePartnerName) return;
        void loadFinanceMethods(financePartnerName);
    }, [financePartnerName, loadFinanceMethods]);

    useEffect(() => {
        if (!financePartnerName || !financeMethodId) return;
        void loadPricingMethods(financePartnerName, financeMethodId);
    }, [financePartnerName, financeMethodId, loadPricingMethods]);

    const handlePartnerChange = (value: string) => {
        setFinancePartnerId(value);
        const nextPartner = financePartners.find((o) => o.value === String(value));
        financeMethodsLoadedForRef.current = '';
        pricingMethodsLoadedForRef.current = '';
        setFinanceMethods([]);
        setPricingMethods([]);
        setFinanceMethodId('');
        setPricingMethod('');
        if (normalizePartnerKey(nextPartner?.label) === normalizePartnerKey(COMMON_CONSTANTS.CashPartnerName)) {
            void loadFinanceMethods(nextPartner?.label || '');
        }
    };

    useEffect(() => {
        if (!confirmQuoteCellKey) {
            updateRRDetails(null);
            return;
        }
        updateRRDetails({
            financePartnerId,
            financePartnerName,
            financeMethodId,
            contract_Type: financeMethodId,
            Finance_Method_Name: financeMethodId,
            PricingMethod: pricingMethod,
            PricingValue: Number(price) || 0,
            DownPayment: Number(downPayment) || 0,
            Deposit_Amount: Number(depositAmount) || 0,
            Financed_Amount: rrBlock.Financed_Amount ?? null,
            RR_System_Size: rrSystemSize || '0',
            TotalSaleAmount: totalSalesAmount,
            valid: formValid,
            validationMessage,
        });
    }, [
        confirmQuoteCellKey,
        financePartnerId,
        financePartnerName,
        financeMethodId,
        pricingMethod,
        price,
        downPayment,
        depositAmount,
        rrSystemSize,
        totalSalesAmount,
        formValid,
        validationMessage,
        rrBlock.Financed_Amount,
        updateRRDetails,
    ]);

    if (!confirmQuoteCellKey) return null;

    const showTotalSales = pricingMethod !== 'EPC' || totalSalesAmountShow;
    const headerStats: GqSectionStat[] = showTotalSales
        ? [{ label: 'Total sales amount', value: `$${formatCurrency(totalSalesAmount)}`, emphasize: true }]
        : [];

    const fieldsDisabled = formLocked;

    return (
        <div className="obd-roof-quote-details__container obd-roof-quote-details__container--split obd-gq-section">
            <div className="obd-rr-quote-details__head-wrap">
                <GenerateQuoteSectionHead title="RR" stats={headerStats} ariaLabel="RR quote totals" />
                {pricingMethod === 'EPC' && (
                    <button
                        type="button"
                        className="obd-rr-quote-details__eye-btn"
                        onClick={() => setTotalSalesAmountShow((v) => !v)}
                        title={totalSalesAmountShow ? 'Hide Total Sales Amount' : 'View Total Sales Amount'}
                        aria-label={totalSalesAmountShow ? 'Hide Total Sales Amount' : 'View Total Sales Amount'}
                    >
                        {totalSalesAmountShow ? <EyeSlash size={18} weight="duotone" /> : <Eye size={18} weight="duotone" />}
                    </button>
                )}
            </div>
            <div className="obd-gq-section-body">
                <div className="obd-generate-quote-split obd-generate-quote-split--no-aside">
                    <div className="obd-generate-quote-split__forms">
                        <div className="obd-roof-quote-details__top-grid">
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">
                                    Finance Partner
                                    <span className="obd-rr-quote-details__required">*</span>
                                </label>
                                <OBDSelect
                                    className="obd-roof-quote-details__select"
                                    value={financePartnerId || undefined}
                                    onChange={handlePartnerChange}
                                    options={financePartners}
                                    disabled
                                    popupClassName={selectDropdownClassName}
                                />
                            </div>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">
                                    Finance Method
                                    <span className="obd-rr-quote-details__required">*</span>
                                </label>
                                <OBDSelect
                                    className="obd-roof-quote-details__select"
                                    value={financeMethodId || undefined}
                                    onChange={(val) => {
                                        pricingMethodsLoadedForRef.current = '';
                                        setFinanceMethodId(val);
                                    }}
                                    options={financeMethods}
                                    disabled={fieldsDisabled}
                                    popupClassName={selectDropdownClassName}
                                />
                            </div>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">Deposit Amount</label>
                                <CurrencyInput
                                    className="obd-roof-quote-details__input"
                                    value={depositAmount}
                                    onValueChange={(val) => setDepositAmount(val ?? '0.00')}
                                    prefix="$"
                                    decimalsLimit={2}
                                    allowNegativeValue={false}
                                    disabled={fieldsDisabled}
                                />
                            </div>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">
                                    Pricing Method
                                    <span className="obd-rr-quote-details__required">*</span>
                                </label>
                                <OBDSelect
                                    className="obd-roof-quote-details__select"
                                    value={pricingMethod || undefined}
                                    onChange={setPricingMethod}
                                    options={pricingMethods}
                                    disabled={fieldsDisabled}
                                    popupClassName={selectDropdownClassName}
                                />
                            </div>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">
                                    Pricing Value
                                    <span className="obd-rr-quote-details__required">*</span>
                                </label>
                                <CurrencyInput
                                    className={`obd-roof-quote-details__input ${showValidation && priceInvalid ? 'ant-input-status-error' : ''}`}
                                    value={price}
                                    onValueChange={(val) => setPrice(val ?? '')}
                                    onBlur={() => setShowValidation(true)}
                                    prefix="$"
                                    decimalsLimit={2}
                                    allowNegativeValue={false}
                                    disabled={fieldsDisabled}
                                />
                                {showValidation && priceInvalid && (
                                    <span className="obd-roof-quote-details__error-msg">Please enter pricing value</span>
                                )}
                            </div>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">Down Payment</label>
                                <CurrencyInput
                                    className="obd-roof-quote-details__input"
                                    value={downPayment}
                                    onValueChange={(val) => setDownPayment(val ?? '0.00')}
                                    prefix="$"
                                    decimalsLimit={2}
                                    allowNegativeValue={false}
                                    disabled={fieldsDisabled}
                                />
                            </div>
                            <div className="obd-roof-quote-details__field-group">
                                <label className="obd-roof-quote-details__label">
                                    R&R System Size in Kilowatts
                                    <span className="obd-rr-quote-details__required">*</span>
                                </label>
                                <Input
                                    className={`obd-roof-quote-details__input ${showValidation && systemSizeInvalid ? 'ant-input-status-error' : ''}`}
                                    value={rrSystemSize}
                                    onChange={(e) => setRrSystemSize(e.target.value)}
                                    onKeyPress={allowDecimalKey}
                                    onBlur={() => setShowValidation(true)}
                                    disabled={fieldsDisabled || !systemSizeEditable}
                                    autoComplete="off"
                                />
                                {showValidation && systemSizeInvalid && (
                                    <span className="obd-roof-quote-details__error-msg">
                                        Please enter R&R System Size in Kilowatts
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RRQuoteDetails;
